/**
 * Rupee formatting.
 *
 * `money()` is the exact figure and stays the default everywhere a number is read to be acted on —
 * a ledger line, a freight row, an invoice. Nobody reconciles against a rounded number.
 *
 * `moneyCompact()` is for DASHBOARD TILES only, where the figure is a headline rather than a
 * statement of account. Past a lakh the full form stops being readable at a glance and starts
 * overflowing its card: `₹1,26,81,709` is nine glyphs of noise where `₹1.27 Cr` is the actual
 * message. Indian grouping, so K → L → Cr rather than the western K/M/B, because that is how the
 * people using this read money.
 *
 * Always pair it with the exact value in a `title=` so hovering still gives the precise number —
 * compacting is a display choice, never a loss of information.
 */

/** Exact, grouped Indian format: ₹1,26,81,709 */
export function money(n: number | string | null | undefined): string {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/** Exact, no symbol. */
export function num(n: number | string | null | undefined): string {
  return Number(n ?? 0).toLocaleString('en-IN');
}

const CRORE = 10_000_000;
const LAKH = 100_000;
const THOUSAND = 1_000;

/**
 * Headline form: ₹1.27 Cr · ₹80.5 L · ₹8.4 K · ₹850
 *
 * One decimal is kept below 100 of a unit (₹1.27 Cr, ₹8.4 K) and dropped above it (₹127 Cr), which
 * is where the decimal stops carrying information. Negatives keep their sign — a postpaid wallet
 * legitimately runs below zero and must not read as positive.
 */
export function moneyCompact(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '₹0';
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);

  const fmt = (value: number, unit: string) => {
    const dp = value < 100 ? 1 : 0;
    // Trim a trailing ".0" — "₹8 K" reads better than "₹8.0 K".
    const s = value.toFixed(dp).replace(/\.0$/, '');
    return `${sign}₹${s} ${unit}`;
  };

  if (a >= CRORE) return fmt(a / CRORE, 'Cr');
  if (a >= LAKH) return fmt(a / LAKH, 'L');
  if (a >= THOUSAND) return fmt(a / THOUSAND, 'K');
  return sign + '₹' + a.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/** Compact count (no ₹): 12.4 K, 1.2 L — for shipment counts on tiles. */
export function numCompact(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '0';
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const fmt = (value: number, unit: string) => `${sign}${value.toFixed(value < 100 ? 1 : 0).replace(/\.0$/, '')} ${unit}`;
  if (a >= CRORE) return fmt(a / CRORE, 'Cr');
  if (a >= LAKH) return fmt(a / LAKH, 'L');
  if (a >= THOUSAND) return fmt(a / THOUSAND, 'K');
  return sign + a.toLocaleString('en-IN');
}
